/**
 * AppPermissionsRiskGauge Component
 *
 * Editorial-palette risk display. The big numeric score sits beside a
 * 4-segment ladder (Low / Medium / High / Critical), where the active tier
 * fills with its semantic tone and lower tiers carry a softer mute. Mirrors
 * the `RiskCapsule` pattern in `<DriftDetailDrawer>` so a risk read on an
 * OAuth app and a drift read of the same tier sit visually adjacent.
 */

interface AppPermissionsRiskGaugeProps {
  score: number | null
  tier: string
}

type TierKey = 'Low' | 'Medium' | 'High' | 'Critical'

const TIER_ORDER: ReadonlyArray<TierKey> = ['Low', 'Medium', 'High', 'Critical']

const TIER_COLOR: Record<TierKey, string> = {
  Low: 'var(--color-green-deep, #4A9B6E)',
  Medium: 'var(--color-blue-deep, #7BA8C4)',
  High: 'var(--color-sand-deep, #C9AD80)',
  Critical: 'var(--color-red-deep, #C84B4B)',
}

function tierIndex(tier: string): number {
  const idx = TIER_ORDER.indexOf(tier as TierKey)
  return idx === -1 ? 0 : idx
}

export function AppPermissionsRiskGauge({ score, tier }: AppPermissionsRiskGaugeProps) {
  if (score === null) return null
  const scorePercent = Math.round(score * 100)
  const activeIdx = tierIndex(tier)
  const activeColor = TIER_COLOR[tier as TierKey] || 'var(--color-mute, #6B7280)'

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-3">
          <span
            className="font-display text-[34px] font-extrabold leading-none tracking-[-0.02em] tabular-nums"
            style={{ color: activeColor }}
          >
            {scorePercent}
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: activeColor }}
          >
            {tier} Risk
          </span>
        </div>
        <span
          className="font-mono text-[11px] font-semibold tabular-nums"
          style={{ color: 'var(--color-faint, #9CA3AF)' }}
        >
          {scorePercent}/100
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {TIER_ORDER.map((t, idx) => {
          const isLitOrBelow = idx <= activeIdx
          return (
            <div
              key={t}
              className="h-1.5 rounded-full"
              style={{
                background: isLitOrBelow
                  ? TIER_COLOR[t]
                  : 'var(--color-rule, #E6E1D5)',
                opacity: isLitOrBelow && idx < activeIdx ? 0.45 : 1,
              }}
            />
          )
        })}
      </div>
    </div>
  )
}
